import React, { useState, useEffect } from 'react';
import { machinesAPI, customersAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { PlusCircle, Edit, Trash2, List, FileText, PauseCircle, PlayCircle, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PasswordModal from '../components/PasswordModal';
import MachineReportModal from '../components/MachineReportModal';
import { verifyAdminPassword } from '../utils/passwordUtils';
import { useSortableTable, SortableTh } from '../utils/useSortableTable';
import MachineSearchBox from '../components/MachineSearchBox';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MachinesPage = () => {
  const [machines, setMachines] = useState([]);
  const [inactiveMachines, setInactiveMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'inactive'
  
  // ADD MODE - Step 1
  const [selectedCatalog, setSelectedCatalog] = useState('');
  const [catalogItem, setCatalogItem] = useState(null);
  
  // ADD MODE - Step 2
  const [formData, setFormData] = useState({
    customer_id: '',
    machine_serial: '',
    reading_method: 'ISS LEX',
    brand: '',
    model: '',
    machine_color: '',
    machine_type: '',
    start_counter_black: 0,
    start_counter_color: 0,
    start_counter_a3: 0,
    start_counter_a3_color: 0,
    price_per_month: 0,
    price_per_page_black: 0,
    price_per_page_color: 0,
    price_per_page_a3: 0,
    price_per_page_a3_color: 0,
    acquisition_cost: 0,
    contract_start: new Date().toISOString().split('T')[0],
    contract_end: new Date().toISOString().split('T')[0],
    contract_no: '',
    extra_equipment: '',
    notes: ''
  });
  
  // EDIT MODE
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState('');
  
  // PAUSE MODE
  const [pauseTarget, setPauseTarget] = useState(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('contract_end');
  const [pauseReasonOther, setPauseReasonOther] = useState('');
  const [pauseDate, setPauseDate] = useState(new Date().toISOString().split('T')[0]);
  
  // REACTIVATE MODE
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  
  // PASSWORD MODAL
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // MACHINE REPORT
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState(null);

  // CHARGE CODES (ΚΩΔΙΚΟΙ ΧΡΕΩΣΗΣ)
  const [showChargeCodesModal, setShowChargeCodesModal] = useState(false);
  const [chargeCodesTarget, setChargeCodesTarget] = useState(null);
  const [chargeCodesLoading, setChargeCodesLoading] = useState(false);
  const [chargeCodes, setChargeCodes] = useState({ monthly: '', black: '', color: '', a3: '', a3_color: '' });

  // BULK CHARGE CODES (μαζική καταχώρηση ανά μοντέλο)
  const [bulkModel, setBulkModel] = useState('');
  const [bulkCodes, setBulkCodes] = useState({ monthly: '', black: '', color: '', a3: '', a3_color: '' });
  
  // LIST VIEW
  const [listCustomer, setListCustomer] = useState('---');
  const { sortConfig, handleSort, sortData } = useSortableTable();
  
  // PDF EXPORT
  const [pdfCustomer, setPdfCustomer] = useState('---');
  const [pdfMachine, setPdfMachine] = useState('---');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [machinesRes, customersRes, catalogRes, inactiveRes] = await Promise.all([
        machinesAPI.getAll(),
        customersAPI.getAll(),
        axios.get(`${API_URL}/catalog`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/machines/filter/inactive`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMachines(machinesRes.data);
      setInactiveMachines(inactiveRes.data);
      setCustomers(customersRes.data);
      setCatalog(catalogRes.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      machine_serial: '',
      reading_method: 'ISS LEX',
      brand: '',
      model: '',
      machine_color: '',
      machine_type: '',
      start_counter_black: 0,
      start_counter_color: 0,
      start_counter_a3: 0,
      start_counter_a3_color: 0,
      price_per_month: 0,
      price_per_page_black: 0,
      price_per_page_color: 0,
      price_per_page_a3: 0,
      price_per_page_a3_color: 0,
      acquisition_cost: 0,
      contract_start: new Date().toISOString().split('T')[0],
      contract_end: new Date().toISOString().split('T')[0],
      contract_no: ''
    });
    setSelectedCatalog('');
    setCatalogItem(null);
    setEditTarget(null);
    setDeleteTarget('');
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  // ADD - Step 1: Catalog selection
  const handleCatalogSelect = (catalogDisplay) => {
    if (catalogDisplay === '-- ΕΠΙΛΟΓΗ --') {
      setSelectedCatalog('');
      setCatalogItem(null);
      return;
    }
    
    setSelectedCatalog(catalogDisplay);
    const item = catalog.find(c => 
      `${c.brand} - ${c.model} (${c.machine_type}, ${c.machine_color})` === catalogDisplay
    );
    
    if (item) {
      setCatalogItem(item);
      setFormData({
        ...formData,
        brand: item.brand,
        model: item.model,
        machine_color: item.machine_color === 'COLOR' ? 'Color' : 'Black',
        machine_type: item.machine_type
      });
    }
  };

  // ADD - Step 2: Submit
  const handleAdd = async (e) => {
    e.preventDefault();
    
    if (!formData.machine_serial.trim()) {
      toast.error('⚠️ ΤΟ Serial Number ΕΙΝΑΙ ΥΠΟΧΡΕΩΤΙΚΟ!');
      return;
    }
    
    const submitData = {
      ...formData,
      machine_serial: formData.machine_serial.trim().toUpperCase()
    };
    
    try {
      await machinesAPI.create(submitData);
      toast.success(`ΤΟ ΜΗΧΑΝΗΜΑ ${submitData.machine_serial} ΚΑΤΑΧΩΡΗΘΗΚΕ!`);
      fetchData();
      setMode(null);
      resetForm();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('❌ ΤΟ Serial Number ΥΠΑΡΧΕΙ ΗΔΗ ΣΤΗ ΒΑΣΗ!');
      } else {
        toast.error('Σφάλμα αποθήκευσης');
      }
    }
  };

  // EDIT
  const handleEditSelect = (sn) => {
    const machine = machines.find(m => m.machine_serial === sn);
    if (machine) {
      setEditTarget(machine);
      setFormData({
        ...machine,
        contract_start: machine.contract_start || new Date().toISOString().split('T')[0],
        contract_end: machine.contract_end || new Date().toISOString().split('T')[0]
      });
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setPendingAction({ type: 'edit' });
    setShowPasswordModal(true);
  };

  const executeEdit = async () => {
    try {
      await machinesAPI.update(editTarget.id, formData);
      toast.success('ΟΙ ΑΛΛΑΓΕΣ ΑΠΟΘΗΚΕΥΤΗΚΑΝ!');
      fetchData();
      setMode(null);
      resetForm();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // DELETE
  const handleDelete = async (e) => {
    e.preventDefault();
    setPendingAction({ type: 'delete' });
    setShowPasswordModal(true);
  };

  const executeDelete = async () => {
    const machine = machines.find(m => m.machine_serial === deleteTarget);
    if (!machine) return;
    
    try {
      // Delete readings first (cascade)
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/readings/machine/${machine.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await machinesAPI.delete(machine.id);
      toast.warning(`ΤΟ ΜΗΧΑΝΗΜΑ ${deleteTarget} ΔΙΑΓΡΑΦΗΚΕ.`);
      fetchData();
      setMode(null);
      resetForm();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const handleGeneratePDF = async () => {
    const machine = machines.find(m => m.machine_serial === pdfMachine);
    if (!machine) {
      toast.error('Μηχάνημα δεν βρέθηκε');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch report data
      const response = await axios.get(`${API_URL}/machines/${machine.id}/report-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
      setShowReportModal(true);
      toast.success('Η καρτέλα δημιουργήθηκε!');
      
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error('Σφάλμα: ' + (error.response?.data?.detail || 'Δοκιμάστε ξανά'));
    } finally {
      setLoading(false);
    }
  };

  // Handler for MachineSearchBox in PDF Export section: directly generates report from a picked machine
  const handleSearchSelectGeneratePDF = async (machine) => {
    if (!machine) return;
    setPdfCustomer(machine.company_name || '---');
    setPdfMachine(machine.machine_serial || '---');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/machines/${machine.id}/report-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
      setShowReportModal(true);
      toast.success('Η καρτέλα δημιουργήθηκε!');
    } catch (error) {
      toast.error('Σφάλμα: ' + (error.response?.data?.detail || 'Δοκιμάστε ξανά'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    // Trigger the modal's own PDF generation
    toast.info('Δημιουργία PDF...');
  };


  // CHARGE CODES handlers
  const handleOpenChargeCodes = async (machine) => {
    setChargeCodesTarget(machine);
    setShowChargeCodesModal(true);
    setChargeCodesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/machines/${machine.id}/charge-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChargeCodes({
        monthly: res.data.monthly || '',
        black: res.data.black || '',
        color: res.data.color || '',
        a3: res.data.a3 || '',
        a3_color: res.data.a3_color || ''
      });
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης κωδικών χρέωσης');
    } finally {
      setChargeCodesLoading(false);
    }
  };

  const handleSaveChargeCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/machines/${chargeCodesTarget.id}/charge-codes`, chargeCodes, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Οι κωδικοί χρέωσης αποθηκεύτηκαν!');
      setShowChargeCodesModal(false);
      setChargeCodesTarget(null);
      await fetchData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης κωδικών χρέωσης');
    }
  };

  // BULK CHARGE CODES handlers
  const getUniqueModels = () => {
    const seen = new Set();
    const list = [];
    [...machines, ...inactiveMachines].forEach(m => {
      const key = `${m.brand}|${m.model}`;
      if (!seen.has(key)) {
        seen.add(key);
        list.push({ key, brand: m.brand, model: m.model });
      }
    });
    return list.sort((a, b) => a.key.localeCompare(b.key));
  };

  const bulkModelMachineCount = bulkModel
    ? machines.filter(m => `${m.brand}|${m.model}` === bulkModel).length
    : 0;

  const handleBulkSaveChargeCodes = async () => {
    if (!bulkModel) {
      toast.error('Επιλέξτε μοντέλο');
      return;
    }
    const [brand, model] = bulkModel.split('|');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_URL}/machines/bulk-charge-codes`,
        { brand, model, codes: bulkCodes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ενημερώθηκαν ${res.data.updated_count} μηχανήματα του μοντέλου ${brand} ${model}!`);
      setBulkModel('');
      setBulkCodes({ monthly: '', black: '', color: '', a3: '', a3_color: '' });
      await fetchData();
    } catch (error) {
      toast.error('Σφάλμα μαζικής αποθήκευσης');
    }
  };

  const handlePauseClick = () => {
    if (mode === 'pause' && machines.length > 0) {
      setShowPauseModal(true);
    }
  };

  const handlePauseConfirm = async () => {
    if (!pauseTarget) {
      toast.error('Επιλέξτε μηχάνημα');
      return;
    }
    
    const reason = pauseReason === 'other' ? pauseReasonOther : getPauseReasonLabel(pauseReason);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/machines/${pauseTarget.id}/deactivate`,
        { reason, deactivated_date: pauseDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Το μηχάνημα ${pauseTarget.machine_serial} τέθηκε σε παύση!`);
      setShowPauseModal(false);
      setPauseTarget(null);
      setPauseReason('contract_end');
      setPauseReasonOther('');
      await fetchData();
    } catch (error) {
      toast.error('Σφάλμα κατά την παύση');
    }
  };

  const handleReactivateClick = (machine) => {
    setReactivateTarget(machine);
    setShowReactivateModal(true);
  };

  const handleReactivateConfirm = async () => {
    if (!reactivateTarget.customer_id) {
      toast.error('Επιλέξτε πελάτη');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // First, reactivate the machine with the new customer
      await axios.post(
        `${API_URL}/machines/${reactivateTarget.id}/reactivate?new_customer_id=${reactivateTarget.customer_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get the last counters from readings
      let lastCounters = {
        start_counter_black: reactivateTarget.start_counter_black || 0,
        start_counter_color: reactivateTarget.start_counter_color || 0,
        start_counter_a3: reactivateTarget.start_counter_a3 || 0,
        start_counter_a3_color: reactivateTarget.start_counter_a3_color || 0
      };
      
      try {
        const readingsRes = await axios.get(
          `${API_URL}/readings/history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Find last reading for this machine
        const machineReadings = readingsRes.data.filter(r => r.machine_id === reactivateTarget.id);
        if (machineReadings.length > 0) {
          // Sort by date descending
          machineReadings.sort((a, b) => {
            const dateA = new Date(a.reading_year, a.reading_month - 1);
            const dateB = new Date(b.reading_year, b.reading_month - 1);
            return dateB - dateA;
          });
          
          const lastReading = machineReadings[0];
          lastCounters = {
            start_counter_black: lastReading.counter_value_black || 0,
            start_counter_color: lastReading.counter_value_color || 0,
            start_counter_a3: lastReading.counter_value_a3 || 0,
            start_counter_a3_color: lastReading.counter_value_a3_color || 0
          };
        }
      } catch (err) {
        console.error('Error fetching last counters:', err);
      }
      
      // Close reactivate modal
      setShowReactivateModal(false);
      
      // Prepare data for edit mode with new customer and last counters
      const today = new Date().toISOString().split('T')[0];
      const oneYearLater = new Date();
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      const oneYearDate = oneYearLater.toISOString().split('T')[0];
      
      const editData = {
        ...reactivateTarget,
        customer_id: reactivateTarget.customer_id,
        ...lastCounters,
        contract_start: today,
        contract_end: oneYearDate,
        // Reset prices to 0 for user to fill
        price_per_month: 0,
        price_per_page_black: 0,
        price_per_page_color: 0,
        price_per_page_a3: 0,
        price_per_page_a3_color: 0,
        acquisition_cost: 0
      };
      
      // Open edit mode automatically
      setEditTarget(reactivateTarget);
      setFormData(editData);
      setMode('edit');
      setReactivateTarget(null);
      
      toast.success(`Το μηχάνημα ${reactivateTarget.machine_serial} επανενεργοποιήθηκε! Συμπληρώστε τα στοιχεία.`);
      
      await fetchData();
    } catch (error) {
      toast.error('Σφάλμα κατά την επανενεργοποίηση');
    }
  };

  const getPauseReasonLabel = (value) => {
    const options = {
      'contract_end': 'Λήξη Συμβολαίου',
      'return': 'Επιστροφή από Πελάτη',
      'service': 'Βλάβη / Service',
      'other': 'Άλλο'
    };
    return options[value] || value;
  };

  const handlePasswordConfirm = async (password) => {
    const isValid = await verifyAdminPassword(password);
    if (isValid) {
      setShowPasswordModal(false);
      if (pendingAction?.type === 'edit') {
        await executeEdit();
      } else if (pendingAction?.type === 'delete') {
        await executeDelete();
      }
      setPendingAction(null);
    }
  };

  // Filter machines by customer
  const getFilteredMachines = () => {
    if (listCustomer === '---') return [];
    if (listCustomer === 'ΟΛΟΙ ΟΙ ΠΕΛΑΤΕΣ') return machines;
    return machines.filter(m => m.company_name === listCustomer);
  };

  const isA3Machine = formData.machine_type?.includes('A3');
  const isA3ColorMachine = isA3Machine && formData.machine_color === 'Color';

  if (loading) {
    return <div className="flex justify-center py-12">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">ΣΤΟΛΟΣ ΜΗΧΑΝΗΜΑΤΩΝ</h1>
      </div>

      {/* Mode Buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
        <Button onClick={() => handleModeChange('add')} className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm">
          <PlusCircle className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΝΕΟ ΜΗΧΑΝΗΜΑ</span>
        </Button>
        <Button onClick={() => handleModeChange('edit')} className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm">
          <Edit className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΔΙΟΡΘΩΣΗ</span>
        </Button>
        <Button onClick={() => handleModeChange('delete')} className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm">
          <Trash2 className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΔΙΑΓΡΑΦΗ</span>
        </Button>
        <Button onClick={() => handleModeChange('pause')} className="bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm">
          <PauseCircle className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΠΑΥΣΗ</span>
        </Button>
        <Button onClick={() => handleModeChange('bulkcodes')} data-testid="mode-bulk-codes" className="bg-amber-600 hover:bg-amber-700 text-xs sm:text-sm">
          <Receipt className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΜΑΖΙΚΟΙ ΚΩΔΙΚΟΙ</span>
        </Button>
        <Button onClick={() => handleModeChange(null)} variant="outline" className="text-xs sm:text-sm col-span-2 sm:col-span-1">
          <List className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΛΙΣΤΑ / Reset</span>
        </Button>
      </div>

      <hr />

      {/* ADD MODE */}
      {mode === 'add' && (
        <>
          {customers.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <p className="text-center text-orange-600">
                  ⚠️ ΔΕΝ ΥΠΑΡΧΟΥΝ ΠΕΛΑΤΕΣ. ΠΡΟΣΘΕΣΤΕ ΠΡΩΤΑ ΠΕΛΑΤΗ.
                </p>
              </CardContent>
            </Card>
          ) : catalog.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <p className="text-center text-orange-600">
                  ⚠️ Ο ΚΑΤΑΛΟΓΟΣ ΜΗΧΑΝΗΜΑΤΩΝ ΕΙΝΑΙ ΚΕΝΟΣ. ΠΡΟΣΘΕΣΤΕ ΠΡΩΤΑ ΜΟΝΤΕΛΑ ΣΤΟΝ ΚΑΤΑΛΟΓΟ (Tab 8).
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader>
                  <CardTitle>🖨️ ΒΗΜΑ 1: ΕΠΙΛΕΞΤΕ ΜΗΧΑΝΗΜΑ ΑΠΟ ΤΟΝ ΚΑΤΑΛΟΓΟ</CardTitle>
                  <p className="text-sm text-gray-600">
                    ΤΑ ΜΗΧΑΝΗΜΑΤΑ ΠΡΕΠΕΙ ΠΡΩΤΑ ΝΑ ΥΠΑΡΧΟΥΝ ΣΤΟΝ ΚΑΤΑΛΟΓΟ ΜΗΧΑΝΗΜΑΤΩΝ (Tab 8).
                  </p>
                </CardHeader>
                <CardContent>
                  <select
                    value={selectedCatalog}
                    onChange={(e) => handleCatalogSelect(e.target.value)}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md"
                  >
                    <option>-- ΕΠΙΛΟΓΗ --</option>
                    {catalog.map((c, idx) => (
                      <option key={idx} value={`${c.brand} - ${c.model} (${c.machine_type}, ${c.machine_color})`}>
                        {c.brand} - {c.model} ({c.machine_type}, {c.machine_color})
                      </option>
                    ))}
                  </select>
                  
                  {catalogItem && (
                    <div className="mt-4 p-4 bg-blue-50 rounded border border-blue-200">
                      <p className="font-semibold">
                        ΜΑΡΚΑ: {catalogItem.brand} | ΜΟΝΤΕΛΟ: {catalogItem.model} | 
                        ΤΥΠΟΣ: {catalogItem.machine_type} | ΧΡΩΜΑ: {catalogItem.machine_color}
                      </p>
                      {catalogItem.consumables && (
                        <p className="text-sm text-gray-700 mt-2">
                          📦 ΑΝΑΛΩΣΙΜΑ: {catalogItem.consumables}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {selectedCatalog && selectedCatalog !== '-- ΕΠΙΛΟΓΗ --' && (
                <Card className="border-l-4 border-l-green-500">
                  <CardHeader>
                    <CardTitle>📄 ΒΗΜΑ 2: ΣΤΟΙΧΕΙΑ ΜΗΧΑΝΗΜΑΤΟΣ & ΣΥΜΒΟΛΑΙΟΥ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAdd} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">ΕΠΙΛΕΞΤΕ ΠΕΛΑΤΗ *</label>
                        <select
                          value={formData.customer_id}
                          onChange={(e) => setFormData({...formData, customer_id: Number(e.target.value)})}
                          className="w-full h-10 px-3 border border-gray-300 rounded-md"
                          required
                        >
                          <option value="">---</option>
                          {customers.map(c => (
                            <option key={c.id} value={c.id}>{c.company_name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="col-span-2">
                          <label className="text-sm font-medium">ΑΡΙΘΜΟΣ ΣΥΜΒΟΛΑΙΟΥ</label>
                          <Input
                            value={formData.contract_no}
                            onChange={(e) => setFormData({...formData, contract_no: e.target.value})}
                            placeholder="Π.Χ. Σ-2026-001"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">ΕΝΑΡΞΗ ΣΥΜΒΟΛΑΙΟΥ</label>
                          <Input
                            type="date"
                            value={formData.contract_start}
                            onChange={(e) => setFormData({...formData, contract_start: e.target.value})}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium">ΛΗΞΗ ΣΥΜΒΑΣΗΣ</label>
                        <Input
                          type="date"
                          value={formData.contract_end}
                          onChange={(e) => setFormData({...formData, contract_end: e.target.value})}
                        />
                      </div>

                      <hr />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Serial Number (S/N) *</label>
                          <Input
                            value={formData.machine_serial}
                            onChange={(e) => setFormData({...formData, machine_serial: e.target.value.toUpperCase()})}
                            placeholder="SERIAL NUMBER"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">ΤΡΟΠΟΣ ΛΗΨΗΣ ΕΝΔΕΙΞΕΩΝ</label>
                          <select
                            value={formData.reading_method}
                            onChange={(e) => setFormData({...formData, reading_method: e.target.value})}
                            className="w-full h-10 px-3 border border-gray-300 rounded-md"
                          >
                            <option>ISS LEX</option>
                            <option>EMAIL</option>
                            <option>PHONE</option>
                            <option>XEPI</option>
                            <option>OTHER</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A4 Black (Start)</label>
                          <Input
                            type="number"
                            value={formData.start_counter_black}
                            onChange={(e) => setFormData({...formData, start_counter_black: Number(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A4 Color (Start)</label>
                          <Input
                            type="number"
                            value={formData.start_counter_color}
                            onChange={(e) => setFormData({...formData, start_counter_color: Number(e.target.value)})}
                          />
                        </div>
                      </div>

                      {isA3Machine && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A3 Black</label>
                            <Input
                              type="number"
                              value={formData.start_counter_a3}
                              onChange={(e) => setFormData({...formData, start_counter_a3: Number(e.target.value)})}
                            />
                          </div>
                          {isA3ColorMachine && (
                            <div>
                              <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A3 Color</label>
                              <Input
                                type="number"
                                value={formData.start_counter_a3_color}
                                onChange={(e) => setFormData({...formData, start_counter_a3_color: Number(e.target.value)})}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium">ΠΑΓΙΟ ΜΗΝΑ (€)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.price_per_month}
                            onChange={(e) => setFormData({...formData, price_per_month: Number(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium" title="Κόστος αγοράς μηχανήματος">ΑΡ.Χ (€)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.acquisition_cost ?? 0}
                            onChange={(e) => setFormData({...formData, acquisition_cost: Number(e.target.value)})}
                            data-testid="acquisition-cost-add"
                            placeholder="Κόστος αγοράς"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">ΤΙΜΗ/ΣΕΛΙΔΑ A4 Black</label>
                          <Input
                            type="number"
                            step="0.0001"
                            value={formData.price_per_page_black}
                            onChange={(e) => setFormData({...formData, price_per_page_black: Number(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">ΤΙΜΗ/ΣΕΛΙΔΑ A4 Color</label>
                          <Input
                            type="number"
                            step="0.0001"
                            value={formData.price_per_page_color}
                            onChange={(e) => setFormData({...formData, price_per_page_color: Number(e.target.value)})}
                          />
                        </div>
                      </div>

                      {isA3Machine && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">ΤΙΜΗ/ΣΕΛΙΔΑ A3 Black</label>
                            <Input
                              type="number"
                              step="0.0001"
                              value={formData.price_per_page_a3}
                              onChange={(e) => setFormData({...formData, price_per_page_a3: Number(e.target.value)})}
                            />
                          </div>
                          {isA3ColorMachine && (
                            <div>
                              <label className="text-sm font-medium">ΤΙΜΗ/ΣΕΛΙΔΑ A3 Color</label>
                              <Input
                                type="number"
                                step="0.0001"
                                value={formData.price_per_page_a3_color}
                                onChange={(e) => setFormData({...formData, price_per_page_a3_color: Number(e.target.value)})}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* NEW FIELDS */}
                      <div>
                        <label className="text-sm font-medium">ΕΞΤΡΑ ΕΞΟΠΛΙΣΜΟΣ</label>
                        <Input
                          value={formData.extra_equipment}
                          onChange={(e) => setFormData({...formData, extra_equipment: e.target.value})}
                          placeholder="π.χ. Fax, Scanner, Finisher"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium">ΣΧΟΛΙΑ</label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                          value={formData.notes}
                          onChange={(e) => setFormData({...formData, notes: e.target.value})}
                          placeholder="Προσθέστε σχόλια ή σημειώσεις..."
                        />
                      </div>

                      <Button type="submit" className="bg-[#004a99] hover:bg-[#003a7a]">
                        ✅ ΟΡΙΣΤΙΚΗ ΑΠΟΘΗΚΕΥΣΗ
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* EDIT MODE */}
      {mode === 'edit' && machines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>✏️ ΕΠΕΞΕΡΓΑΣΙΑ ΜΗΧΑΝΗΜΑΤΟΣ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">ΑΝΑΖΗΤΗΣΗ ΜΗΧΑΝΗΜΑΤΟΣ</label>
                <MachineSearchBox
                  machines={machines}
                  customers={customers}
                  onSelect={(m) => handleEditSelect(m.machine_serial)}
                  placeholder="Πληκτρολογήστε S/N, επωνυμία ή ΑΦΜ..."
                />
                <p className="text-xs text-gray-400 mt-1">ή επιλέξτε από τη λίστα:</p>
              </div>
              <div>
                <label className="text-sm font-medium">ΕΠΙΛΕΞΤΕ ΜΗΧΑΝΗΜΑ ΓΙΑ ΔΙΟΡΘΩΣΗ</label>
                <select
                  onChange={(e) => handleEditSelect(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                >
                  <option value="">---</option>
                  {machines.map(m => (
                    <option key={m.id} value={m.machine_serial}>
                      {m.company_name} - {m.brand} {m.model} ({m.machine_serial})
                    </option>
                  ))}
                </select>
              </div>

              {editTarget && (
                <form onSubmit={handleEdit} className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded">
                    <p className="font-medium">ΕΠΕΞΕΡΓΑΣΙΑ ΣΤΟΙΧΕΙΩΝ: {editTarget.brand} {editTarget.model} ({editTarget.machine_serial})</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">ΣΕΙΡΙΑΚΟ ΝΟΥΜΕΡΟ (S/N)</label>
                      <Input
                        value={formData.machine_serial}
                        onChange={(e) => setFormData({...formData, machine_serial: e.target.value.toUpperCase()})}
                        placeholder="π.χ. 7018820000PX2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΤΥΠΟΣ ΜΗΧΑΝΗΜΑΤΟΣ</label>
                      <select
                        value={formData.machine_type}
                        onChange={(e) => setFormData({...formData, machine_type: e.target.value})}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      >
                        <option>MFP A4</option>
                        <option>PRINTER A4</option>
                        <option>MFP A3</option>
                        <option>PRINTER A3</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">ΜΑΡΚΑ / ΜΟΝΤΕΛΟ (από Κατάλογο)</label>
                      <select
                        value={`${formData.brand}|${formData.model}`}
                        onChange={(e) => {
                          const [brand, model] = e.target.value.split('|');
                          const cat = catalog.find(c => c.brand === brand && c.model === model);
                          setFormData({
                            ...formData,
                            brand: brand,
                            model: model,
                            machine_type: cat?.machine_type || formData.machine_type,
                            machine_color: cat?.machine_color || formData.machine_color
                          });
                        }}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      >
                        {catalog.map((c, idx) => (
                          <option key={idx} value={`${c.brand}|${c.model}`}>
                            {c.brand} - {c.model} ({c.machine_type}, {c.machine_color})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΧΡΩΜΑ</label>
                      <select
                        value={formData.machine_color}
                        onChange={(e) => setFormData({...formData, machine_color: e.target.value})}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      >
                        <option>Black</option>
                        <option>Color</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΠΕΛΑΤΗΣ</label>
                      <select
                        value={formData.customer_id}
                        onChange={(e) => setFormData({...formData, customer_id: Number(e.target.value)})}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      >
                        {customers.map(c => (
                          <option key={c.id} value={c.id}>{c.company_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">ΤΡΟΠΟΣ ΛΗΨΗΣ</label>
                      <select
                        value={formData.reading_method}
                        onChange={(e) => setFormData({...formData, reading_method: e.target.value})}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      >
                        <option>ISS LEX</option>
                        <option>EMAIL</option>
                        <option>PHONE</option>
                        <option>XEPI</option>
                        <option>OTHER</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΜΗΝΙΑΙΟ ΠΑΓΙΟ</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price_per_month}
                        onChange={(e) => setFormData({...formData, price_per_month: Number(e.target.value)})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium" title="Κόστος αγοράς μηχανήματος">ΑΡ.Χ (€)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.acquisition_cost ?? 0}
                        onChange={(e) => setFormData({...formData, acquisition_cost: Number(e.target.value)})}
                        data-testid="acquisition-cost-edit"
                        placeholder="Κόστος αγοράς"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">ΤΙΜΗ A4 Black</label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={formData.price_per_page_black}
                        onChange={(e) => setFormData({...formData, price_per_page_black: Number(e.target.value)})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΤΙΜΗ A4 Color</label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={formData.price_per_page_color}
                        onChange={(e) => setFormData({...formData, price_per_page_color: Number(e.target.value)})}
                      />
                    </div>
                  </div>

                  {isA3Machine && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">ΤΙΜΗ A3 Black</label>
                        <Input
                          type="number"
                          step="0.0001"
                          value={formData.price_per_page_a3}
                          onChange={(e) => setFormData({...formData, price_per_page_a3: Number(e.target.value)})}
                        />
                      </div>
                      {isA3ColorMachine && (
                        <div>
                          <label className="text-sm font-medium">ΤΙΜΗ A3 Color</label>
                          <Input
                            type="number"
                            step="0.0001"
                            value={formData.price_per_page_a3_color}
                            onChange={(e) => setFormData({...formData, price_per_page_a3_color: Number(e.target.value)})}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm font-medium">ΑΡ. ΣΥΜΒΟΛΑΙΟΥ</label>
                      <Input
                        value={formData.contract_no}
                        onChange={(e) => setFormData({...formData, contract_no: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΕΝΑΡΞΗ</label>
                      <Input
                        type="date"
                        value={formData.contract_start}
                        onChange={(e) => setFormData({...formData, contract_start: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">ΛΗΞΗ</label>
                    <Input
                      type="date"
                      value={formData.contract_end}
                      onChange={(e) => setFormData({...formData, contract_end: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A4 Black</label>
                      <Input
                        type="number"
                        value={formData.start_counter_black}
                        onChange={(e) => setFormData({...formData, start_counter_black: Number(e.target.value)})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A4 Color</label>
                      <Input
                        type="number"
                        value={formData.start_counter_color}
                        onChange={(e) => setFormData({...formData, start_counter_color: Number(e.target.value)})}
                      />
                    </div>
                  </div>

                  {isA3Machine && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A3 Black</label>
                        <Input
                          type="number"
                          value={formData.start_counter_a3}
                          onChange={(e) => setFormData({...formData, start_counter_a3: Number(e.target.value)})}
                        />
                      </div>
                      {isA3ColorMachine && (
                        <div>
                          <label className="text-sm font-medium">ΑΡΧΙΚΟΣ ΜΕΤΡΗΤΗΣ A3 Color</label>
                          <Input
                            type="number"
                            value={formData.start_counter_a3_color}
                            onChange={(e) => setFormData({...formData, start_counter_a3_color: Number(e.target.value)})}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* NEW FIELDS */}
                  <div>
                    <label className="text-sm font-medium">ΕΞΤΡΑ ΕΞΟΠΛΙΣΜΟΣ</label>
                    <Input
                      value={formData.extra_equipment}
                      onChange={(e) => setFormData({...formData, extra_equipment: e.target.value})}
                      placeholder="π.χ. Fax, Scanner, Finisher"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΣΧΟΛΙΑ</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Προσθέστε σχόλια ή σημειώσεις..."
                    />
                  </div>

                  <Button type="submit" className="bg-[#004a99] hover:bg-[#003a7a]">
                    💾 ΑΠΟΘΗΚΕΥΣΗ ΑΛΛΑΓΩΝ
                  </Button>
                </form>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* DELETE MODE */}
      {mode === 'delete' && machines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>🗑️ ΔΙΑΓΡΑΦΗ ΜΗΧΑΝΗΜΑΤΟΣ</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDelete} className="space-y-4">
              <div>
                <label className="text-sm font-medium">ΜΗΧΑΝΗΜΑ ΠΡΟΣ ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ</label>
                <select
                  value={deleteTarget}
                  onChange={(e) => setDeleteTarget(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">---</option>
                  {machines.map(m => (
                    <option key={m.id} value={m.machine_serial}>
                      {m.company_name} - {m.brand} {m.model} ({m.machine_serial})
                    </option>
                  ))}
                </select>
              </div>

              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                🚨 ΔΙΑΓΡΑΦΗ
              </Button>
            </form>
          </CardContent>
        </Card>
      )}


      {/* PAUSE MODE */}
      {mode === 'pause' && (
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader>
            <CardTitle>⏸️ ΠΑΥΣΗ ΜΗΧΑΝΗΜΑΤΟΣ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Επιλέξτε ένα μηχάνημα για να το θέσετε σε παύση (δεν θα εμφανίζεται στις ενεργές λίστες).
              </p>
              <div>
                <label className="text-sm font-medium mb-2 block">ΑΝΑΖΗΤΗΣΗ ΜΗΧΑΝΗΜΑΤΟΣ</label>
                <MachineSearchBox
                  machines={machines}
                  customers={customers}
                  onSelect={(m) => setPauseTarget(m)}
                  placeholder="Πληκτρολογήστε S/N, επωνυμία ή ΑΦΜ..."
                />
                <p className="text-xs text-gray-400 mt-1">ή επιλέξτε από τη λίστα:</p>
              </div>
              <div>
                <label className="text-sm font-medium">ΕΠΙΛΕΞΤΕ ΜΗΧΑΝΗΜΑ</label>
                <select
                  value={pauseTarget?.machine_serial || ''}
                  onChange={(e) => {
                    const machine = machines.find(m => m.machine_serial === e.target.value);
                    setPauseTarget(machine);
                  }}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                >
                  <option value="">---</option>
                  {machines.map(m => (
                    <option key={m.id} value={m.machine_serial}>
                      {m.company_name} - {m.brand} {m.model} ({m.machine_serial})
                    </option>
                  ))}
                </select>
              </div>
              
              {pauseTarget && (
                <Button onClick={handlePauseClick} className="bg-orange-600 hover:bg-orange-700">
                  ⏸️ ΠΑΥΣΗ ΜΗΧΑΝΗΜΑΤΟΣ
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* BULK CHARGE CODES MODE */}
      {mode === 'bulkcodes' && (
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              🧾 ΜΑΖΙΚΗ ΚΑΤΑΧΩΡΗΣΗ ΚΩΔΙΚΩΝ ΧΡΕΩΣΗΣ (ΑΝΑ ΜΟΝΤΕΛΟ)
            </CardTitle>
            <p className="text-sm text-gray-600">
              Επιλέξτε ένα μοντέλο και ορίστε τους κωδικούς μία φορά. Θα εφαρμοστούν σε ΟΛΑ τα ενεργά μηχανήματα αυτού του μοντέλου. Αφήστε κενά όσα δεν χρεώνονται.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">ΜΟΝΤΕΛΟ</label>
              <select
                value={bulkModel}
                onChange={(e) => setBulkModel(e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-md"
                data-testid="bulk-model-select"
              >
                <option value="">-- ΕΠΙΛΟΓΗ ΜΟΝΤΕΛΟΥ --</option>
                {getUniqueModels().map(m => (
                  <option key={m.key} value={m.key}>{m.brand} - {m.model}</option>
                ))}
              </select>
              {bulkModel && (
                <p className="text-xs text-amber-700 mt-1 font-medium">
                  Θα ενημερωθούν {bulkModelMachineCount} ενεργά μηχανήματα αυτού του μοντέλου.
                </p>
              )}
            </div>

            {bulkModel && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">💶 Μηνιαίο Πάγιο / Μίσθωμα</label>
                    <Input
                      value={bulkCodes.monthly}
                      onChange={(e) => setBulkCodes({ ...bulkCodes, monthly: e.target.value })}
                      placeholder="π.χ. 008490"
                      data-testid="bulk-code-monthly"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">⬛ Ασπρόμαυρη Α4</label>
                    <Input
                      value={bulkCodes.black}
                      onChange={(e) => setBulkCodes({ ...bulkCodes, black: e.target.value })}
                      placeholder="π.χ. 008488"
                      data-testid="bulk-code-black"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">🎨 Έγχρωμη Α4</label>
                    <Input
                      value={bulkCodes.color}
                      onChange={(e) => setBulkCodes({ ...bulkCodes, color: e.target.value })}
                      placeholder="π.χ. 008489"
                      data-testid="bulk-code-color"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">⬛ Ασπρόμαυρη Α3</label>
                    <Input
                      value={bulkCodes.a3}
                      onChange={(e) => setBulkCodes({ ...bulkCodes, a3: e.target.value })}
                      placeholder="Κωδικός (προαιρετικό)"
                      data-testid="bulk-code-a3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">🎨 Έγχρωμη Α3</label>
                    <Input
                      value={bulkCodes.a3_color}
                      onChange={(e) => setBulkCodes({ ...bulkCodes, a3_color: e.target.value })}
                      placeholder="Κωδικός (προαιρετικό)"
                      data-testid="bulk-code-a3-color"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleBulkSaveChargeCodes}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="bulk-codes-save"
                >
                  💾 ΕΦΑΡΜΟΓΗ ΣΕ {bulkModelMachineCount} ΜΗΧΑΝΗΜΑΤΑ
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* LIST VIEW */}
      {!mode && (
        <>
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
                activeTab === 'active'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📋 ΕΝΕΡΓΑ ({machines.length})
            </button>
            <button
              onClick={() => setActiveTab('inactive')}
              className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
                activeTab === 'inactive'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              💤 ΑΝΕΝΕΡΓΑ ({inactiveMachines.length})
            </button>
          </div>

          {/* ACTIVE MACHINES */}
          {activeTab === 'active' && (
            <Card>
              <CardHeader>
                <CardTitle>📋 ΕΝΕΡΓΟΣ ΣΤΟΛΟΣ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">👤 ΕΠΙΛΕΞΤΕ ΠΕΛΑΤΗ</label>
                  <select
                    value={listCustomer}
                    onChange={(e) => setListCustomer(e.target.value)}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md"
                  >
                    <option>---</option>
                    <option>ΟΛΟΙ ΟΙ ΠΕΛΑΤΕΣ</option>
                    {customers.map(c => (
                      <option key={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>

                {listCustomer !== '---' && getFilteredMachines().length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-gray-100">
                        <tr>
                          <SortableTh columnKey="company_name" sortConfig={sortConfig} onSort={handleSort} className="border px-3 py-2">ΠΕΛΑΤΗΣ</SortableTh>
                          <SortableTh columnKey="brand" sortConfig={sortConfig} onSort={handleSort} className="border px-3 py-2">ΜΑΡΚΑ</SortableTh>
                          <SortableTh columnKey="model" sortConfig={sortConfig} onSort={handleSort} className="border px-3 py-2">ΜΟΝΤΕΛΟ</SortableTh>
                          <SortableTh columnKey="machine_serial" sortConfig={sortConfig} onSort={handleSort} className="border px-3 py-2">S/N</SortableTh>
                          <SortableTh columnKey="machine_type" sortConfig={sortConfig} onSort={handleSort} className="border px-3 py-2">ΤΥΠΟΣ</SortableTh>
                          <SortableTh columnKey="machine_color" sortConfig={sortConfig} onSort={handleSort} className="border px-3 py-2">ΧΡΩΜΑ</SortableTh>
                          <th className="border px-3 py-2">ΚΩΔΙΚΟΙ ΧΡΕΩΣΗΣ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortData(getFilteredMachines()).map(m => (
                          <tr key={m.id} className="hover:bg-gray-50">
                            <td className="border px-3 py-2">{m.company_name}</td>
                            <td className="border px-3 py-2">{m.brand}</td>
                            <td className="border px-3 py-2">{m.model}</td>
                            <td className="border px-3 py-2 font-mono">{m.machine_serial}</td>
                            <td className="border px-3 py-2">{m.machine_type}</td>
                            <td className="border px-3 py-2">{m.machine_color}</td>
                            <td className="border px-3 py-2 text-center">
                              {(() => {
                                const cc = m.charge_codes || {};
                                const hasCodes = ['monthly','black','color','a3','a3_color'].some(k => (cc[k] || '').toString().trim());
                                return (
                                  <div className="flex items-center justify-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleOpenChargeCodes(m)}
                                      data-testid={`charge-codes-btn-${m.id}`}
                                      className="text-xs"
                                    >
                                      <Receipt className="h-4 w-4 mr-1" />
                                      Κωδικοί
                                    </Button>
                                    {hasCodes ? (
                                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded" data-testid={`charge-codes-status-${m.id}`} title="Έχει κωδικούς χρέωσης">✓ ΟΚ</span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded" data-testid={`charge-codes-status-${m.id}`} title="Δεν έχει κωδικούς χρέωσης">⚠ ΧΩΡΙΣ</span>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {listCustomer !== '---' && getFilteredMachines().length === 0 && (
                  <p className="text-center py-6 text-gray-500">Δεν βρέθηκαν μηχανήματα για αυτόν τον πελάτη.</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* INACTIVE MACHINES */}
          {activeTab === 'inactive' && (
            <Card>
              <CardHeader>
                <CardTitle>💤 ΑΝΕΝΕΡΓΑ ΜΗΧΑΝΗΜΑΤΑ</CardTitle>
              </CardHeader>
              <CardContent>
                {inactiveMachines.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">Δεν υπάρχουν ανενεργά μηχανήματα.</p>
                ) : (
                  <div className="space-y-3">
                    {inactiveMachines.map(m => (
                      <div key={m.id} className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                        <div className="flex justify-between items-start">
                          <div className="space-y-1">
                            <p className="font-semibold text-lg">{m.brand} {m.model}</p>
                            <p className="text-sm text-gray-600">Serial: <span className="font-mono">{m.machine_serial}</span></p>
                            <p className="text-sm text-gray-600">Τελευταίος Πελάτης: {m.company_name}</p>
                            <p className="text-sm text-orange-700">
                              <span className="font-semibold">Status:</span> 💤 ΑΝΕΝΕΡΓΟ 
                              {m.deactivated_date && ` (από ${m.deactivated_date})`}
                            </p>
                            {m.deactivation_reason && (
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold">Λόγος:</span> {m.deactivation_reason}
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={() => handleReactivateClick(m)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <PlayCircle className="h-4 w-4 mr-2" />
                            ΕΠΑΝΕΝΕΡΓΟΠΟΙΗΣΗ
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* PDF EXPORT SECTION */}
          {/* PDF EXPORT Section */}
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                📄 ΕΞΑΓΩΓΗ ΚΑΡΤΕΛΑΣ ΜΗΧΑΝΗΜΑΤΟΣ (PDF)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* QUICK SEARCH BOX - search by S/N, customer name, AFM */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <label className="text-sm font-medium text-blue-900 block mb-2">⚡ ΓΡΗΓΟΡΗ ΑΝΑΖΗΤΗΣΗ ΜΗΧΑΝΗΜΑΤΟΣ</label>
                <MachineSearchBox
                  machines={machines}
                  customers={customers}
                  onSelect={handleSearchSelectGeneratePDF}
                  placeholder="S/N, Πελάτης, ΑΦΜ, Brand, Model... (έξοδος καρτέλας)"
                />
                <p className="text-xs text-gray-500 mt-1">Επιλέξτε μηχάνημα για άμεση δημιουργία καρτέλας PDF</p>
              </div>

              <div className="text-center text-xs text-gray-400 -my-1">— ή —</div>

              <div>
                <label className="text-sm font-medium">👤 1. ΕΠΙΛΕΞΤΕ ΠΕΛΑΤΗ</label>
                <select
                  value={pdfCustomer}
                  onChange={(e) => {
                    setPdfCustomer(e.target.value);
                    setPdfMachine('---');
                  }}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                >
                  <option>---</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.company_name}>{c.company_name}</option>
                  ))}
                </select>
              </div>

              {pdfCustomer !== '---' && (() => {
                const customerMachines = machines.filter(m => m.company_name === pdfCustomer);
                return customerMachines.length === 0 ? (
                  <p className="text-orange-600">⚠️ Ο ΠΕΛΑΤΗΣ ΔΕΝ ΕΧΕΙ ΜΗΧΑΝΗΜΑΤΑ.</p>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-medium">🖥️ 2. ΕΠΙΛΕΞΤΕ ΜΗΧΑΝΗΜΑ</label>
                      <select
                        value={pdfMachine}
                        onChange={(e) => setPdfMachine(e.target.value)}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      >
                        <option>---</option>
                        {customerMachines.map(m => (
                          <option key={m.id} value={m.machine_serial}>
                            {m.brand} - {m.model} - [SN: {m.machine_serial}]
                          </option>
                        ))}
                      </select>
                    </div>

                    {pdfMachine !== '---' && (
                      <Button 
                        onClick={handleGeneratePDF}
                        className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700"
                        disabled={loading}
                      >
                        <FileText className="h-5 w-5" />
                        {loading ? 'Δημιουργία...' : '📥 ΔΗΜΙΟΥΡΓΙΑ ΚΑΡΤΕΛΑΣ PDF'}
                      </Button>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </>
      )}
      
      {/* Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title="Επιβεβαίωση Κωδικού Admin"
      />
      
      {/* PAUSE MODAL */}
      {showPauseModal && pauseTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>⏸️ ΠΑΥΣΗ ΜΗΧΑΝΗΜΑΤΟΣ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-blue-50 rounded">
                <p className="text-sm font-semibold">{pauseTarget.brand} {pauseTarget.model}</p>
                <p className="text-xs text-gray-600">S/N: {pauseTarget.machine_serial}</p>
                <p className="text-xs text-gray-600">Πελάτης: {pauseTarget.company_name}</p>
              </div>

              <div>
                <label className="text-sm font-medium">Λόγος Παύσης</label>
                <select
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md mt-1"
                >
                  <option value="contract_end">Λήξη Συμβολαίου</option>
                  <option value="return">Επιστροφή από Πελάτη</option>
                  <option value="service">Βλάβη / Service</option>
                  <option value="other">Άλλο (προσδιορίστε)</option>
                </select>
              </div>

              {pauseReason === 'other' && (
                <div>
                  <Input
                    value={pauseReasonOther}
                    onChange={(e) => setPauseReasonOther(e.target.value)}
                    placeholder="Προσδιορίστε τον λόγο..."
                    required
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Ημερομηνία Παύσης</label>
                <Input
                  type="date"
                  value={pauseDate}
                  onChange={(e) => setPauseDate(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowPauseModal(false);
                    setPauseTarget(null);
                    setPauseReason('contract_end');
                    setPauseReasonOther('');
                  }}
                >
                  ΑΚΥΡΟ
                </Button>
                <Button 
                  onClick={handlePauseConfirm}
                  className="bg-orange-600 hover:bg-orange-700"
                  disabled={pauseReason === 'other' && !pauseReasonOther}
                >
                  ✅ ΠΑΥΣΗ ΜΗΧΑΝΗΜΑΤΟΣ
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* REACTIVATE MODAL */}
      {showReactivateModal && reactivateTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>🔄 ΕΠΑΝΕΝΕΡΓΟΠΟΙΗΣΗ ΜΗΧΑΝΗΜΑΤΟΣ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-green-50 rounded">
                <p className="text-sm font-semibold">{reactivateTarget.brand} {reactivateTarget.model}</p>
                <p className="text-xs text-gray-600">S/N: {reactivateTarget.machine_serial}</p>
                <p className="text-xs text-orange-600">Προηγούμενος Πελάτης: {reactivateTarget.company_name}</p>
              </div>

              <p className="text-sm text-gray-700">
                Επιλέξτε τον πελάτη στον οποίο θα ανατεθεί το μηχάνημα:
              </p>

              <div>
                <label className="text-sm font-medium">Πελάτης</label>
                <select
                  value={reactivateTarget.customer_id || ''}
                  onChange={(e) => setReactivateTarget({...reactivateTarget, customer_id: parseInt(e.target.value)})}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md mt-1"
                >
                  <option value="">Επιλέξτε πελάτη...</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.company_name} {c.id === reactivateTarget.customer_id && '(Προηγούμενος)'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>ℹ️ Αυτόματη Ενέργεια:</strong><br/>
                  Μετά την επιβεβαίωση, θα ανοίξει αυτόματα η φόρμα επεξεργασίας με:<br/>
                  • <strong>Μετρητές:</strong> Από το τελευταίο reading<br/>
                  • <strong>Συμβόλαιο:</strong> Νέες ημερομηνίες (σήμερα + 1 έτος)<br/>
                  • <strong>Τιμές:</strong> Συμπληρώστε τις νέες τιμές
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowReactivateModal(false);
                    setReactivateTarget(null);
                  }}
                >
                  ΑΚΥΡΟ
                </Button>
                <Button 
                  onClick={handleReactivateConfirm}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={!reactivateTarget.customer_id}
                >
                  ✅ ΕΠΑΝΕΝΕΡΓΟΠΟΙΗΣΗ & ΕΠΕΞΕΡΓΑΣΙΑ
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* CHARGE CODES MODAL */}
      {showChargeCodesModal && chargeCodesTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="charge-codes-modal">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Κωδικοί Χρέωσης
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-blue-50 rounded">
                <p className="text-sm font-semibold">{chargeCodesTarget.brand} {chargeCodesTarget.model}</p>
                <p className="text-xs text-gray-600">S/N: {chargeCodesTarget.machine_serial}</p>
                <p className="text-xs text-gray-600">Πελάτης: {chargeCodesTarget.company_name}</p>
              </div>

              <p className="text-sm text-gray-600">
                Αντιστοιχίστε κάθε μετρητή και το μηνιαίο πάγιο με τον κωδικό χρέωσης του λογιστικού. Αφήστε κενά όσα δεν χρειάζονται.
              </p>

              {chargeCodesLoading ? (
                <div className="text-center py-6 text-gray-500">Φόρτωση...</div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">💶 Μηνιαίο Πάγιο / Μίσθωμα</label>
                    <Input
                      value={chargeCodes.monthly}
                      onChange={(e) => setChargeCodes({ ...chargeCodes, monthly: e.target.value })}
                      placeholder="π.χ. 008490"
                      data-testid="charge-code-monthly"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">⬛ Ασπρόμαυρη Εκτύπωση Α4</label>
                    <Input
                      value={chargeCodes.black}
                      onChange={(e) => setChargeCodes({ ...chargeCodes, black: e.target.value })}
                      placeholder="π.χ. 008488"
                      data-testid="charge-code-black"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">🎨 Έγχρωμη Εκτύπωση Α4</label>
                    <Input
                      value={chargeCodes.color}
                      onChange={(e) => setChargeCodes({ ...chargeCodes, color: e.target.value })}
                      placeholder="π.χ. 008489"
                      data-testid="charge-code-color"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">⬛ Ασπρόμαυρη Εκτύπωση Α3</label>
                    <Input
                      value={chargeCodes.a3}
                      onChange={(e) => setChargeCodes({ ...chargeCodes, a3: e.target.value })}
                      placeholder="Κωδικός (προαιρετικό)"
                      data-testid="charge-code-a3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">🎨 Έγχρωμη Εκτύπωση Α3</label>
                    <Input
                      value={chargeCodes.a3_color}
                      onChange={(e) => setChargeCodes({ ...chargeCodes, a3_color: e.target.value })}
                      placeholder="Κωδικός (προαιρετικό)"
                      data-testid="charge-code-a3-color"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowChargeCodesModal(false);
                    setChargeCodesTarget(null);
                  }}
                  data-testid="charge-codes-cancel"
                >
                  ΑΚΥΡΟ
                </Button>
                <Button
                  onClick={handleSaveChargeCodes}
                  className="bg-[#004a99] hover:bg-[#003a7a]"
                  disabled={chargeCodesLoading}
                  data-testid="charge-codes-save"
                >
                  💾 ΑΠΟΘΗΚΕΥΣΗ
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Machine Report Modal */}
      <MachineReportModal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportData(null);
        }}
        reportData={reportData}
        onDownloadPDF={handleDownloadPDF}
      />
    </div>
  );
};

export default MachinesPage;
